/**
 * Mutation hooks for RouteChallenge operations
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { RouteChallengeTeam, createRouteChallengeTeam } from '../Model/Engagement/RouteChallenge/RouteChallengeTeam';

export function useSaveRouteChallengeTeam() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (team: RouteChallengeTeam) =>
      backend
        .post('/coach-program/engagement/route-challenges/teams', {
          teamId: team.id,
          challengeId: team.challengeId,
          name: team.name,
          image: team.image,
        })
        .then((response) => createRouteChallengeTeam(response)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeChallengeTeam'] });
      queryClient.invalidateQueries({ queryKey: ['routeChallenge'] });
    },
  });
}

export function useDeleteRouteChallengeTeam() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => backend.delete(`/coach-program/engagement/route-challenges/teams/${teamId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeChallengeTeam'] });
      queryClient.invalidateQueries({ queryKey: ['routeChallenge'] });
    },
  });
}

export function useJoinRouteChallengeTeam() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, inviteCode }: { teamId: string; inviteCode?: string }) =>
      backend
        .post(`/coach-program/engagement/route-challenges/teams/${teamId}/join`, { inviteCode })
        .then((response) => createRouteChallengeTeam(response)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeChallengeTeam'] });
      queryClient.invalidateQueries({ queryKey: ['routeChallenge'] });
    },
  });
}

export function useLeaveRouteChallengeTeam() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) =>
      backend.post(`/coach-program/engagement/route-challenges/teams/${teamId}/leave`, {}).then((response) => createRouteChallengeTeam(response)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeChallengeTeam'] });
      queryClient.invalidateQueries({ queryKey: ['routeChallenge'] });
    },
  });
}

export function useRemoveRouteChallengeTeamMember() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      backend
        .delete(`/coach-program/engagement/route-challenges/teams/${teamId}/members/${memberId}`)
        .then((response) => createRouteChallengeTeam(response)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeChallengeTeam'] });
      queryClient.invalidateQueries({ queryKey: ['routeChallenge'] });
    },
  });
}
